import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
// Import necessary types from the library
import { DateRange, Range, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// --- Custom Hook with Types ---

/**
 * Custom hook to detect clicks outside of a referenced component.
 * @param handler - Function to call when a click outside is detected.
 */
const useClickOutside = (handler: () => void) => {
  // Specify the element type for the ref
  const domNode = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Type the event as a MouseEvent
    const maybeHandler = (event: MouseEvent) => {
      // Check if the ref is mounted and the click is outside
      if (
        domNode.current && 
        !domNode.current.contains(event.target as Node) &&
        document.contains(event.target as Node)
      ) {
        handler();
      }
    };

    document.addEventListener('mousedown', maybeHandler);

    return () => {
      document.removeEventListener('mousedown', maybeHandler);
    };
  }); // Note: Original code had no dependency array. Consider adding [handler].

  return domNode;
};

// --- Component with Types ---

/**
 * Define the props interface for the component.
 */
interface DateRangePickerProps {
  dateRange: Range[];
  setDateRange: React.Dispatch<React.SetStateAction<Range[]>>;
  marginFromViewport?: number;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateRange,
  setDateRange,
  marginFromViewport = 8,
}) => {
  // Type the state
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const inputRef = useRef<HTMLButtonElement>(null);
  const [pickerStyle, setPickerStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    zIndex: 1000,
    border: '1px solid lightgray',
    borderRadius: '4px',
    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
    backgroundColor: 'white',
    opacity: 0,
    transform: 'scale(0.95) translateY(-10px)',
    transition: 'opacity 0.2s ease, transform 0.2s ease',
  });

  // The ref type is inferred from the custom hook
  const datePickerRef = useClickOutside(() => {
    setShowPicker(false);
  });

  const computeAndSetPickerPosition = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    const inputRect = input.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Static dimensions for the picker, adjust if needed
    const pickerWidth = 600; // Base width for one month
    const pickerHeight = 350; // Estimated height for the date range picker
    const months = 1.13; // Using two months in the horizontal layout

    const estimatedPickerWidth = pickerWidth * months;
    const estimatedPickerHeight = pickerHeight;

    // Calculate available space below and above the input field
    const spaceBelow = viewportHeight - inputRect.bottom - marginFromViewport;
    const spaceAbove = inputRect.top - marginFromViewport;

    let top = inputRect.bottom + marginFromViewport;
    let left = inputRect.left;

    // Handle vertical positioning logic
    if (
      spaceBelow < estimatedPickerHeight &&
      spaceAbove >= estimatedPickerHeight
    ) {
      top = inputRect.top - estimatedPickerHeight - marginFromViewport;
    } else if (
      spaceBelow < estimatedPickerHeight &&
      spaceAbove < estimatedPickerHeight
    ) {
      top =
        spaceBelow >= spaceAbove
          ? inputRect.bottom + marginFromViewport
          : inputRect.top - estimatedPickerHeight - marginFromViewport;
    }

    // Horizontal centering logic: Try to center the picker, then adjust if necessary
    left = Math.max(
      marginFromViewport,
      Math.min(left, viewportWidth - estimatedPickerWidth - marginFromViewport),
    );

    // Adjust the left position if the picker overflows on the right side
    if (left + estimatedPickerWidth > viewportWidth - marginFromViewport) {
      left = viewportWidth - estimatedPickerWidth - marginFromViewport;
    }

    // Ensure the picker doesn't overflow on the left side
    if (left < marginFromViewport) {
      left = marginFromViewport;
    }

    // Make sure the picker is fully within the viewport (both top and left)
    top = Math.max(
      marginFromViewport,
      Math.min(
        top,
        viewportHeight - estimatedPickerHeight - marginFromViewport,
      ),
    );

    // Update the picker style with the calculated position
    setPickerStyle((prev) => ({
      ...prev,
      left: Math.round(left),
      top: Math.round(top),
    }));
  }, [marginFromViewport]);

  useEffect(() => {
    if (!showPicker) return;

    // Initial position calculation
    computeAndSetPickerPosition();

    // Start animation after a small delay to ensure positioning is done
    const animationTimeout = setTimeout(() => {
      setPickerStyle((prev) => ({
        ...prev,
        opacity: 1,
        transform: 'scale(1) translateY(0)',
      }));
    }, 10);

    // Handle window changes
    const handleWindowChange = () => {
      computeAndSetPickerPosition();
    };

    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);

    return () => {
      clearTimeout(animationTimeout);
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [showPicker, computeAndSetPickerPosition]);

  useEffect(() => {
    if (!showPicker || !datePickerRef.current) return;

    const observer = new ResizeObserver(() => {
      computeAndSetPickerPosition();
    });

    observer.observe(datePickerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [showPicker, computeAndSetPickerPosition]);

  /**
   * Handles the date change event from react-date-range.
   * @param item - The date range selection object.
   */
  const handleDateChange = (item: RangeKeyDict) => {
    // item.selection is the Range object, matching our state type
    const newRange = [item.selection];
    if (newRange[0].startDate && newRange[0].endDate) {
      setDateRange(newRange);
    }
    // setDateRange(newRange);
  };

  const handleInputClick = () => {
    if (!showPicker) {
      setShowPicker(true);
    }
  };

  const defaultRange = React.useMemo(
    () => [{ startDate: new Date(), endDate: new Date(), key: 'selection' }],
    []
  );

  return (
    <div className="relative">
      <div
        ref={inputRef as any}
        onClick={handleInputClick}
        className="px-2 py-[10px] border border-primary rounded-md text-xs text-primary bg-white cursor-pointer flex items-center justify-between min-w-[150px]"
      >
        <span>
          {dateRange[0].startDate && dateRange[0].endDate
            ? `${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`
            : 'Select Date Range'}
        </span>
        {dateRange[0].startDate && dateRange[0].endDate && (
          <X
            size={14}
            className="ml-2 text-red-500 hover:text-red-700 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setDateRange([{ startDate: undefined, endDate: undefined, key: 'selection' }]);
            }}
          />
        )}
      </div>

      {showPicker && (
        <div
          ref={datePickerRef} // Assign the ref to the picker's wrapper
          style={pickerStyle}
          // className="absolute right-0 mt-2 bg-white rounded-lg shadow-xl z-50 inset-auto"
        >
          <DateRange
            editableDateInputs={true}
            onChange={handleDateChange}
            moveRangeOnFirstSelection={false}
            ranges={
              dateRange[0].startDate && dateRange[0].endDate
                ? dateRange
                : defaultRange
            }
            months={2}
            direction="horizontal"
            className="text-sm"
            maxDate={new Date()}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
